/**
 * Escuta da palavra de ativação pelo reconhecedor de fala LOCAL do Chrome
 * (spec 011). Sem modelo no bundle, sem licença, sem mexer no PWA.
 *
 * A regra que não pode quebrar: **nada de áudio sai do computador**. O
 * `SpeechRecognition` do Chrome, por padrão, manda o áudio ao Google. Só o
 * modo `processLocally = true` com o pacote pt-BR INSTALADO roda na máquina
 * (teste de campo de 24/09/2026: com o pacote baixando, o offline falhou; com
 * `available()` = 'available', transcreveu com a rede caída). Por isso:
 *  - sem a API de modo local no protótipo → não escuta ('sem-suporte');
 *  - `available()` diferente de 'available' → não escuta ('instalar');
 *  - erro `network` com modo local ligado = foi à rede → para de vez.
 * Conferido a CADA vez que a escuta liga, não uma vez só: o pacote pode
 * sumir com atualização do Chrome ou limpeza do perfil.
 *
 * O texto transcrito vai direto pro `onFinal` e morre lá — não é guardado.
 */

export type LocalSpeechStatus = 'available' | 'downloadable' | 'downloading' | 'unavailable' | 'sem-api';

// A API de modo local é nova (Chrome 139+) e não está nos tipos do TS.
interface LocalRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  processLocally: boolean;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0?: { transcript: string } }> }) => void) | null;
  start: () => void;
  abort: () => void;
}
interface LocalRecognitionCtor {
  new (): LocalRecognition;
  prototype: object;
  available?: (o: { langs: string[]; processLocally: boolean }) => Promise<string>;
  install?: (o: { langs: string[]; processLocally: boolean }) => Promise<boolean>;
}

function getCtor(): LocalRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: LocalRecognitionCtor; webkitSpeechRecognition?: LocalRecognitionCtor };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
  if (!Ctor || !('processLocally' in Ctor.prototype) || typeof Ctor.available !== 'function') return null;
  return Ctor;
}

export async function localSpeechStatus(lang = 'pt-BR'): Promise<LocalSpeechStatus> {
  const Ctor = getCtor();
  if (!Ctor?.available) return 'sem-api';
  try {
    const r = await Ctor.available({ langs: [lang], processLocally: true });
    return r === 'available' || r === 'downloadable' || r === 'downloading' ? r : 'unavailable';
  } catch {
    return 'sem-api';
  }
}

/** Baixa o pacote local. O Chrome exige um clique do usuário pra isso. */
export async function installLocalSpeech(lang = 'pt-BR'): Promise<boolean> {
  const Ctor = getCtor();
  if (!Ctor?.install) return false;
  try {
    return await Ctor.install({ langs: [lang], processLocally: true });
  } catch {
    return false;
  }
}

/** Erros que não adianta tentar de novo. `network` com modo local = foi à rede. */
const FATAIS = new Set(['language-not-supported', 'service-not-allowed', 'not-allowed', 'audio-capture', 'network']);

export type WakeListenerState = 'verificando' | 'ouvindo' | 'instalar' | 'sem-suporte' | 'erro';

export interface WakeListenerOptions {
  lang?: string;
  /** Trecho final transcrito. Não guardar. */
  onFinal: (texto: string) => void;
  /** Parcial (só a tela de teste usa — mostra o que está sendo ouvido). */
  onInterim?: (texto: string) => void;
  onState: (state: WakeListenerState, detalhe?: string) => void;
  /** A sessão do reconhecedor terminou e foi religada. */
  onRestart?: () => void;
}

const BACKOFF_MIN_MS = 250;
const BACKOFF_MAX_MS = 5000;
/** Sessão que durou menos que isto e acabou = algo errado: espera mais pra religar. */
const SESSAO_SAUDAVEL_MS = 5000;

/** Liga a escuta contínua. Devolve o "desliga". */
export function startWakeListener(o: WakeListenerOptions): () => void {
  const lang = o.lang ?? 'pt-BR';
  let stopped = false;
  let rec: LocalRecognition | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let backoff = BACKOFF_MIN_MS;
  let startedAt = 0;
  let fatal: string | null = null;

  const startRec = () => {
    if (stopped || !rec) return;
    fatal = null;
    startedAt = Date.now();
    try {
      rec.start();
    } catch (err) {
      // InvalidStateError: ainda encerrando a anterior. Tenta de novo já já.
      console.warn('[wake] start recusado, tentando de novo:', err);
      timer = setTimeout(startRec, backoff);
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
    }
  };

  void (async () => {
    o.onState('verificando');
    const status = await localSpeechStatus(lang);
    if (stopped) return;
    if (status !== 'available') {
      o.onState(status === 'downloadable' || status === 'downloading' ? 'instalar' : 'sem-suporte', status);
      return;
    }
    const Ctor = getCtor();
    if (!Ctor) {
      o.onState('sem-suporte');
      return;
    }
    rec = new Ctor();
    rec.lang = lang;
    rec.processLocally = true;
    rec.continuous = true;
    rec.interimResults = !!o.onInterim;
    rec.maxAlternatives = 1;
    rec.onstart = () => o.onState('ouvindo');
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        const texto = r?.[0]?.transcript ?? '';
        if (!texto) continue;
        if (r.isFinal) o.onFinal(texto);
        else o.onInterim?.(texto);
      }
    };
    rec.onerror = (e) => {
      if (FATAIS.has(e.error)) fatal = e.error;
    };
    rec.onend = () => {
      if (stopped) return;
      if (fatal) {
        console.warn('[wake] escuta parada: %s', fatal);
        o.onState('erro', fatal);
        return;
      }
      backoff = Date.now() - startedAt > SESSAO_SAUDAVEL_MS ? BACKOFF_MIN_MS : Math.min(backoff * 2, BACKOFF_MAX_MS);
      o.onRestart?.();
      timer = setTimeout(startRec, backoff);
    };
    startRec();
  })();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (rec) {
      rec.onend = null;
      rec.onresult = null;
      rec.onerror = null;
      rec.onstart = null;
      try {
        rec.abort();
      } catch {
        /* ignore */
      }
    }
  };
}
